import { apiClient } from '../lib/apiClient';

/**
 * Confidence level reported by the AI vision identification for a single candidate.
 */
export type LivestockIdentificationConfidence = 'high' | 'medium' | 'low';

/**
 * Taxonomy is capped at the recognisable depth — any level may be absent when
 * the AI could not confidently resolve it (e.g. family known, genus/species unknown).
 */
export interface LivestockIdentificationTaxonomy {
  family?: string;
  genus?: string;
  species?: string;
}

/**
 * A single ranked identification candidate returned by the identify endpoint.
 * Species-level naming is only ever present when the backend's confidence +
 * explanation-quality guard allowed it through — this client treats it as opaque data.
 */
export interface LivestockIdentificationCandidate {
  category: string;
  taxonomy: LivestockIdentificationTaxonomy;
  commonNames: string[];
  confidence: LivestockIdentificationConfidence;
  featureExplanation: string;
}

/**
 * Response payload from POST /api/v1/aquariums/:aquariumId/livestock/identify,
 * already unwrapped from the `{ data: ... }` envelope.
 */
export interface LivestockIdentificationResponse {
  candidates: LivestockIdentificationCandidate[];
  tempPhotoKey: string;
}

/**
 * Parameters for submitting a photo for identification.
 */
export interface IdentifyLivestockPhotoParams {
  aquariumId: string;
  file: File;
  /** The UI language (en, de, …): common names and explanations come back in it. */
  language?: string;
}

interface IdentifyLivestockPhotoApiResponse {
  data: LivestockIdentificationResponse;
}

/**
 * Submits a photo to the AI identification endpoint for the given aquarium.
 * Sends a multipart/form-data request; the field name matches the backend
 * controller's multer configuration (`photo`, matching the measurement photo
 * extraction convention this endpoint mirrors).
 *
 * No DB rows are created by this call — the response contains only ephemeral
 * candidates and a temp S3 key, to be used later by an accept/save flow.
 */
export async function identifyLivestockPhoto(
  params: IdentifyLivestockPhotoParams,
): Promise<LivestockIdentificationResponse> {
  const formData = new FormData();
  if (params.language) formData.append('language', params.language);
  formData.append('photo', params.file);

  const response = await apiClient.post<IdentifyLivestockPhotoApiResponse>(
    `/api/v1/aquariums/${encodeURIComponent(params.aquariumId)}/livestock/identify`,
    formData,
  );

  return response.data;
}

/**
 * Deletes an unaccepted temp photo uploaded during identification (e.g. the
 * user cancelled or closed the dialog with no match). The key is URL-encoded
 * as a single path segment since temp keys contain slashes
 * (`temp/{aquariumId}/{uuid}`).
 */
export async function deleteTempPhoto(aquariumId: string, key: string): Promise<void> {
  await apiClient.delete<void>(
    `/api/v1/aquariums/${encodeURIComponent(aquariumId)}/livestock/temp-photos/${encodeURIComponent(key)}`,
  );
}

/** One suggestion as it was shown to the user (no explanation text is stored). */
export interface ShownIdentificationCandidate {
  category: string;
  taxonomy: LivestockIdentificationTaxonomy;
  commonNames: string[];
  confidence: LivestockIdentificationConfidence;
}

/**
 * Outcome of one identification: the suggestion the user confirmed (accepted), the name
 * they gave the animal themselves (corrected), or that nothing matched (no_match).
 */
export interface LivestockIdentificationFeedback {
  outcome: 'accepted' | 'corrected' | 'no_match';
  candidates: ShownIdentificationCandidate[];
  chosenCandidateIndex?: number | null;
  commonName?: string | null;
  scientificName?: string | null;
  category?: 'coral' | 'fish' | 'invertebrate' | 'other' | null;
  livestockId?: string | null;
  photoKey?: string | null;
  language?: string | null;
}

/**
 * Records an identification outcome (POST .../livestock/identify/feedback). The backend
 * stores it for the signed-in user and sends that user's own confirmed names as context
 * with their later identifications.
 */
export async function submitIdentificationFeedback(
  aquariumId: string,
  feedback: LivestockIdentificationFeedback,
): Promise<void> {
  await apiClient.post<unknown>(
    `/api/v1/aquariums/${encodeURIComponent(aquariumId)}/livestock/identify/feedback`,
    feedback,
  );
}

// ─── Name lookup ──────────────────────────────────────────────────────────────
// POST /api/v1/aquariums/:aquariumId/livestock/name-lookup (src/services/livestockNameLookup.service.ts):
// a typed (trade) name → scientific name, category, coral care group and care hints.

export type LivestockCategory = 'coral' | 'fish' | 'invertebrate' | 'other';
export type CareLevel = 'low' | 'medium' | 'high';

export interface LivestockNameMatch {
  readonly commonName: string | null;
  readonly tradeName: string | null;
  readonly scientificName: string | null;
  readonly category: LivestockCategory | null;
  /** Coral care group (soft / lps / sps / nps); null for other animals. */
  readonly careGroup: 'soft' | 'lps' | 'sps' | 'nps' | null;
  readonly light: CareLevel | null;
  readonly flow: CareLevel | null;
  readonly difficulty: 'easy' | 'moderate' | 'difficult' | null;
  readonly confidence: LivestockIdentificationConfidence;
  readonly explanation: string;
}

/** null = the name was not recognised. */
export async function lookupLivestockName(aquariumId: string, name: string, category?: LivestockCategory | null): Promise<LivestockNameMatch | null> {
  const res = await apiClient.post<{ data: { match: LivestockNameMatch | null } }>(
    `/api/v1/aquariums/${encodeURIComponent(aquariumId)}/livestock/name-lookup`,
    category ? { name, category } : { name },
  );
  return res?.data?.match ?? null;
}

/** "SPS · viel Licht · starke Strömung · anspruchsvoll" — the care hints of a match as one line. */
export function careSummary(match: LivestockNameMatch, t: (key: string) => string): string {
  return [
    match.careGroup ? t(`livestock.lookup.group.${match.careGroup}`) : null,
    match.light ? t(`livestock.lookup.light.${match.light}`) : null,
    match.flow ? t(`livestock.lookup.flow.${match.flow}`) : null,
    match.difficulty ? t(`livestock.lookup.difficulty.${match.difficulty}`) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}
